import logError from 'Helpers/logError';
import Actions from './Actions';
import { AIChatState, Dispatch, AIChatMessage } from './AIChatContext';
import AIChatResource, { AIChatResponse } from 'Resources/AIChat';
import AIScoringResource from 'App/resources/AIScoring';
import LocalStorage from 'Components/Helpers/LocalStorage';

export const LOCAL_STORAGE_KEY = 'ai-chat-messages';
export const CACHE_EXPIRY_HOURS = 24;

export const setMessages = (messages: AIChatMessage[], chatId: string) => (dispatch: Dispatch) => {
	dispatch({ type: Actions.SET_MESSAGES, messages, chatId });
};

const handleResponse = (dispatch: Dispatch, res: AIChatResponse, newMessages: AIChatMessage[], chatId?: string) => {
	if (res.cancelled) {
		return;
	}
	const lastMessage = newMessages[newMessages.length - 1];

	// Check if lastMessage exists before accessing its properties
	if (!lastMessage) {
		console.warn('AIChat: handleResponse called with empty messages array');
		return;
	}

	if (res.action) {
		lastMessage.action = res.action;
	}

	// Set message response even if streamed (backend sends full message when request completes)
	if (res.message) {
		lastMessage.response = res.message;
	}

	// Store langsmithRunId from HTTP response (backup if streaming didn't work)
	if (res.langsmithRunId && !lastMessage.langsmithRunId) {
		lastMessage.langsmithRunId = res.langsmithRunId;
	}

	// Always mark streaming as complete when request is done
	lastMessage.streaming = false;
	lastMessage.loading = false;

	// Use chatId from response if available (where backend actually stored it)
	// Otherwise use provided chatId (the one we sent)
	// This ensures messages are stored in the correct chat without switching active chat
	const targetChatId = res.chatId || chatId;
	dispatch({ type: Actions.SET_MESSAGES, messages: newMessages, chatId: targetChatId });
};

export const switchChat = (chatId: string) => async (dispatch: Dispatch, state: AIChatState) => {
	try {
		const res = await AIChatResource.getChat(chatId);
		dispatch({ type: Actions.SET_CURRENT_CHAT_ID, chatId });
		// Backend already marks chat as read in getChat endpoint, but update local state too
		dispatch({ type: Actions.MARK_CHAT_AS_READ, chatId });

		const localStorage = new LocalStorage();
		const stored = localStorage.getValue(LOCAL_STORAGE_KEY) as string | null;
		if (stored) {
			try {
				const allChats = JSON.parse(stored) as Record<string, { messages: AIChatMessage[]; timestamp: number }>;
				if (allChats[chatId]) {
					dispatch({ type: Actions.SET_MESSAGES, messages: allChats[chatId].messages, chatId });
				} else {
					dispatch({
						type: Actions.SET_MESSAGES,
						messages: res.messages.map((m: any, index: number) => ({
							id: m.id || `${chatId}-${index}-${Date.now()}`,
							message: m.role === 'user' ? m.content : undefined,
							response: m.role === 'assistant' ? m.content : undefined,
							loading: false,
							streaming: false,
							reasoning: m.reasoning
						})),
						chatId
					});
				}
			} catch (err) {
				console.error(err);
			}
		} else {
			dispatch({
				type: Actions.SET_MESSAGES,
				messages: res.messages.map((m: any, index: number) => ({
					id: m.id || `${chatId}-${index}-${Date.now()}`,
					message: m.role === 'user' ? m.content : undefined,
					response: m.role === 'assistant' ? m.content : undefined,
					loading: false,
					streaming: false,
					reasoning: m.reasoning
				})),
				chatId
			});
		}
	} catch (err) {
		logError(err, 'Error switching chat');
	}
};

export const loadChats = () => async (dispatch: Dispatch, state: AIChatState) => {
	try {
		const res = await AIChatResource.listChats();
		// Use unreadCount from backend (stored in Redis)
		const chatsWithUnread = res.chats.map(c => ({
			...c,
			unreadCount: c.unreadCount || 0
		}));
		dispatch({ type: Actions.SET_CHATS, chats: chatsWithUnread });

		// If no current chat is set, load the latest chat
		if (!state.currentChatId && chatsWithUnread.length > 0) {
			const latestChat = chatsWithUnread.sort(
				(a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0)
			)[0];
			await switchChat(latestChat.id)(dispatch, state);
		}

		// Broadcast unread count update
		if (typeof Tools !== 'undefined' && Tools.$rootScope) {
			const totalUnread = chatsWithUnread.reduce(
				(sum, c) => sum + (c.id === state.currentChatId ? 0 : c.unreadCount || 0),
				0
			);
			Tools.$rootScope.$broadcast('AIChat.unreadCount', totalUnread);
		}
	} catch (err) {
		logError(err, 'Error loading chats');
	}
};

export const doSend =
	({ message, isRetry = false }: { message: string; isRetry?: boolean }) =>
	async (dispatch: Dispatch, state: AIChatState) => {
		let newMessages: AIChatMessage[];
		let messageIndex: number;
		let chatId = state.currentChatId;
		let isNewChat = false;

		if (isRetry) {
			// Find the last message with this content and mark it as loading again
			newMessages = [...state.messages];
			messageIndex = newMessages.length - 1;
			for (let i = newMessages.length - 1; i >= 0; i--) {
				if (newMessages[i].message === message && newMessages[i].hasError) {
					messageIndex = i;
					newMessages[i] = { ...newMessages[i], loading: true, hasError: false };
					break;
				}
			}
		} else {
			// Create new chat if none exists - wait for it to be created before proceeding
			if (!chatId) {
				try {
					const newChat = await AIChatResource.createChat();
					dispatch({ type: Actions.SET_CURRENT_CHAT_ID, chatId: newChat.chatId });
					dispatch({
						type: Actions.ADD_CHAT,
						chat: {
							id: newChat.chatId,
							name: newChat.name,
							createdAt: newChat.createdAt,
							updatedAt: newChat.createdAt,
							unreadCount: 0
						}
					});
					chatId = newChat.chatId;
					isNewChat = true;
				} catch (err) {
					logError(err, 'Error creating chat');
					return; // Don't proceed if chat creation failed
				}
			}

			// Generate unique message ID for this message
			const messageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

			newMessages = [...state.messages, { id: messageId, message, loading: true }];
			messageIndex = newMessages.length - 1;
			dispatch({ type: Actions.SET_MESSAGE, message: '' });
		}

		// Ensure chatId is set before storing messages
		if (!chatId) {
			console.error('doSend: No chatId available');
			return;
		}

		dispatch({ type: Actions.SET_MESSAGES, messages: newMessages, chatId });
		dispatch({ type: Actions.SET_LOADING, loading: true, chatId });

		AIChatResource.chat({ message, chatId })
			.then(res => {
				// Use res.chatId for storing messages (where backend actually stored them)
				// But DON'T update currentChatId - only store messages in the correct place
				// This prevents the active chat from switching when backend returns a different chatId
				// Pass res.chatId to handleResponse so messages are stored where backend put them
				const responseChatId = res.chatId || chatId;
				handleResponse(dispatch, res, newMessages, responseChatId || undefined);

				// Mark as read when user sends a message - use the chatId we're currently viewing
				if (chatId) {
					dispatch({ type: Actions.MARK_CHAT_AS_READ, chatId });
				}

				if (isNewChat) {
					loadChats()(dispatch, state);
				}
			})
			.catch(err => {
				logError(err, 'Error sending message to AI Chat');
				// Mark the specific message as failed
				dispatch({ type: Actions.SET_MESSAGE_ERROR, messageIndex });
			})
			.finally(() => {
				const finalChatId = chatId || state.currentChatId;
				if (finalChatId) {
					dispatch({ type: Actions.SET_LOADING, loading: false, chatId: finalChatId });
				}
			});
	};

export const sendMessage = () => (dispatch: Dispatch, state: AIChatState) => {
	const { message } = state;
	if (!message) return;

	doSend({ message })(dispatch, state);
};

export const setMessage = (message: string) => (dispatch: Dispatch, state: AIChatState) => {
	dispatch({ type: Actions.SET_MESSAGE, message });
};

export const clearMessages = (chatId: string) => (dispatch: Dispatch, state: AIChatState) => {
	dispatch({ type: Actions.SET_FEEDBACK, messageIndex: -1, feedbackType: null });
	dispatch({ type: Actions.CLEAR_MESSAGES, chatId: chatId || state.currentChatId || undefined });
	AIChatResource.clearMessages(chatId);

	if (chatId || state.currentChatId) {
		const localStorage = new LocalStorage();
		const stored = localStorage.getValue(LOCAL_STORAGE_KEY) as string | null;
		if (stored) {
			try {
				const allChats = JSON.parse(stored) as Record<string, { messages: AIChatMessage[]; timestamp: number }>;
				const targetChatId = chatId || state.currentChatId;
				if (targetChatId && allChats[targetChatId]) {
					delete allChats[targetChatId];
					localStorage.setValue(LOCAL_STORAGE_KEY, JSON.stringify(allChats));
				}
			} catch (err) {
				console.error(err);
			}
		}
	}
};

export const createChat = () => async (dispatch: Dispatch) => {
	try {
		const res = await AIChatResource.createChat();
		dispatch({
			type: Actions.ADD_CHAT,
			chat: { id: res.chatId, name: res.name, createdAt: res.createdAt, updatedAt: res.createdAt, unreadCount: 0 }
		});
		dispatch({ type: Actions.SET_CURRENT_CHAT_ID, chatId: res.chatId });
		dispatch({ type: Actions.SET_MESSAGES, messages: [], chatId: res.chatId });
	} catch (err) {
		logError(err, 'Error creating chat');
	}
};

export const deleteChat = (chatId: string) => async (dispatch: Dispatch) => {
	try {
		await AIChatResource.deleteChat(chatId);
		dispatch({ type: Actions.DELETE_CHAT, chatId });

		const localStorage = new LocalStorage();
		const stored = localStorage.getValue(LOCAL_STORAGE_KEY) as string | null;
		if (stored) {
			try {
				const allChats = JSON.parse(stored) as Record<string, { messages: AIChatMessage[]; timestamp: number }>;
				delete allChats[chatId];
				localStorage.setValue(LOCAL_STORAGE_KEY, JSON.stringify(allChats));
			} catch (err) {
				console.error(err);
			}
		}
	} catch (err) {
		logError(err, 'Error deleting chat');
	}
};

export const renameChat = (chatId: string, name: string) => async (dispatch: Dispatch) => {
	try {
		const res = await AIChatResource.renameChat(chatId, name);
		dispatch({ type: Actions.UPDATE_CHAT, chatId: res.chatId, updates: { name: res.name, updatedAt: Date.now() } });
	} catch (err) {
		logError(err, 'Error renaming chat');
	}
};

export const setReasoning =
	(reasoning: string, chatId: string, messageId: string) => (dispatch: Dispatch, state: AIChatState) => {
		dispatch({ type: Actions.SET_REASONING_FOR_MESSAGE_ID, chatId, messageId, reasoning });
	};

export const retryMessage = (message: string) => (dispatch: Dispatch, state: AIChatState) => {
	doSend({ message, isRetry: true })(dispatch, state);
};

export const submitFeedback =
	(messageIndex: number, feedbackType: 'up' | 'down', feedbackText?: string) => async (dispatch: Dispatch, state: AIChatState) => {
		const message = state.messages[messageIndex];
		if (state.feedbackGiven[messageIndex] || (!message?.response && !message?.action)) {
			return;
		}

		const score = feedbackType === 'up' ? 10 : -10;
		const extraData = {
			userMessage: message.message,
			response: message.response,
			action: message.action,
			feedbackText,
			langsmithRunId: message.langsmithRunId
		};

		try {
			await AIScoringResource.save({
				type: 'ai_chat_feedback',
				score,
				extra: JSON.stringify(extraData)
			});

			// Mark feedback as given for this message
			dispatch({ type: Actions.SET_FEEDBACK, messageIndex, feedbackType });
		} catch (error) {
			logError(error, 'Failed to submit AI chat feedback');
		}
	};

export const setSidebarExpanded = (expanded: boolean) => (dispatch: Dispatch) => {
	dispatch({ type: Actions.SET_SIDEBAR_EXPANDED, expanded });
};
